/**
 * A program is a series whose editions are events (`series.data.child_type =
 * 'event'`, each edition's `parent_id` the series). Its run reads off its
 * editions' start dates.
 */
import { toDate } from "./dates.js"

/** Whether a program still runs: its series' `data.status` isn't `ended`. The
 *  one rule every tab reads. */
export const programActive = (status: unknown): boolean => status !== "ended"

/** One edition; `record` is the caller's own event row, handed back on the
 *  last and next editions so a card can link to them. */
export interface ProgramEdition<R = unknown> {
  name: string | null
  start: Date | string | null | undefined
  record?: R
}

export interface DatedEdition<R = unknown> {
  name: string | null
  start: Date
  record?: R
}

export interface ProgramSummary<R = unknown> {
  active: boolean
  /** "since 2015" while it runs, "2024 – 2025" once it has ended. */
  running: string
  editions: number
  last: DatedEdition<R> | null
  next: DatedEdition<R> | null
}

export interface ProgramInput<R = unknown> {
  /** The series' `data.status`: `active` or `ended`. */
  status: unknown
  /** The series' `lifetime` time. */
  lifetime?: { start_at?: string | null; end_at?: string | null } | null
  editions: ProgramEdition<R>[]
}

/** A program's card: whether it runs, its years, and its last and next editions. */
export function programSummary<R = unknown>(
  { status, lifetime, editions }: ProgramInput<R>,
  now = new Date()
): ProgramSummary<R> {
  const dated = editions
    .flatMap((e): DatedEdition<R>[] => {
      const start = toDate(e.start)
      return start ? [{ name: e.name, start, record: e.record }] : []
    })
    .sort((a, b) => a.start.getTime() - b.start.getTime())
  const last = dated.filter((e) => e.start <= now).at(-1) ?? null
  const next = dated.find((e) => e.start > now) ?? null
  const active = programActive(status)
  const fromYear = (toDate(lifetime?.start_at) ?? dated[0]?.start)?.getUTCFullYear()
  const toYear = (toDate(lifetime?.end_at) ?? last?.start)?.getUTCFullYear()
  let running: string
  if (active) running = fromYear ? `since ${fromYear}` : ""
  else if (fromYear && toYear && fromYear !== toYear) running = `${fromYear} – ${toYear}`
  else running = String(fromYear ?? toYear ?? "")
  return { active, running, editions: editions.length, last, next }
}

/** Active programs first, then ended ones, each by name. */
export function comparePrograms(
  a: { active: boolean; name?: string | null },
  b: { active: boolean; name?: string | null }
): number {
  return Number(b.active) - Number(a.active) || (a.name ?? "").localeCompare(b.name ?? "")
}
